import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  ScaleIcon,
  PlusIcon,
  CheckCircleIcon,
  ClockIcon,
  Cog6ToothIcon,
  FunnelIcon,
} from '../icons';
import { courtAPI, transcriptionAPI } from '../services/api';
import {
  buildCasesFromTranscriptions,
  caseDetailPath,
  filterCourtCases,
  normalizeCourtCase,
} from '../utils/courtCases';
import { parseTranscriptionRecord, formatDate } from '../utils/transcriptionMeta';
import PageLoadingState from '../components/layout/PageLoadingState';
import KpiStatCard from '../components/layout/KpiStatCard';
import { useTheme } from '../contexts/ThemeContext';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'closed', label: 'Closed' },
  { value: 'pending', label: 'Pending' },
];

const COLUMN_DEFS = [
  { key: 'docket', label: 'Docket' },
  { key: 'title', label: 'Title' },
  { key: 'status', label: 'Status' },
  { key: 'filed', label: 'Filed' },
  { key: 'sessions', label: 'Sessions' },
];

const DEFAULT_VISIBLE = COLUMN_DEFS.map((c) => c.key);

const statusBadgeClass = (status) => {
  const map = {
    active: 'bg-matdash-success-subtle text-emerald-800',
    closed: 'bg-matdash-secondary-subtle text-sky-800',
    pending: 'bg-matdash-warning-subtle text-amber-900',
  };
  return map[status] || 'bg-matdash-primary-subtle text-matdash-primary';
};

const CourtCases = () => {
  const navigate = useNavigate();
  const { dark } = useTheme();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [usingFallback, setUsingFallback] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newDocket, setNewDocket] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const loadCases = async () => {
    setLoading(true);
    setError('');
    setUsingFallback(false);

    try {
      const response = await courtAPI.getCourtCases({ per_page: 200 });
      const items = response.data?.data || response.data || [];
      if (Array.isArray(items)) {
        setCases(items.map(normalizeCourtCase));
        return;
      }
      throw new Error('No cases returned');
    } catch {
      try {
        const txResp = await transcriptionAPI.getTranscriptions({ page: 1, per_page: 500 });
        const list = txResp.data?.data || txResp.data || [];
        const parsed = list.map(parseTranscriptionRecord);
        const derived = buildCasesFromTranscriptions(parsed);
        setCases(derived);
        setUsingFallback(true);
        if (derived.length === 0) {
          setError('No court cases found. Create a case or link a recording to a docket number.');
        }
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to load court cases');
        setCases([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCases();
  }, []);

  const filteredCases = useMemo(
    () => filterCourtCases(cases, { search, status: statusFilter, dateFrom, dateTo }),
    [cases, search, statusFilter, dateFrom, dateTo]
  );

  const kpiStats = useMemo(() => ({
    total: cases.length,
    active: cases.filter((c) => c.status === 'active').length,
    closed: cases.filter((c) => c.status === 'closed').length,
    pending: cases.filter((c) => c.status === 'pending').length,
  }), [cases]);

  const totalPages = Math.max(1, Math.ceil(filteredCases.length / perPage));
  const pageRows = filteredCases.slice((page - 1) * perPage, page * perPage);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, dateFrom, dateTo, perPage]);

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      if (prev.includes(key)) {
        if (prev.length <= 2) return prev;
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  };

  const showCol = (key) => visibleColumns.includes(key);
  const activeFilterCount = [statusFilter !== 'all'].filter(Boolean).length;

  const inputCls = `px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-matdash-primary/40
    ${dark ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900'}`;
  const searchCls = `${inputCls} w-full`;
  const dateCls = `${inputCls} w-[150px] shrink-0`;
  const toolBtn = `inline-flex items-center justify-center h-9 px-3 rounded-lg border text-sm ${
    dark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
  }`;
  const dropdownPanel = 'absolute right-0 top-full pt-1 z-20 hidden group-hover:block';
  const dropdownInner = `w-64 p-3 rounded-xl border shadow-lg space-y-3 ${
    dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
  }`;

  const clearFilters = () => {
    setStatusFilter('all');
    setPage(1);
  };

  const exportCsv = () => {
    const headers = ['Docket', 'Title', 'Status', 'Filed', 'Sessions'];
    const lines = filteredCases.map((row) =>
      [
        row.docket_number,
        row.title,
        row.status,
        formatDate(row.created_at) || '',
        row.transcription_count ?? '',
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(',')
    );
    const csv = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'court-cases.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported as CSV');
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(filteredCases, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'court-cases.json';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported as JSON');
  };

  const handleCreateCase = async (e) => {
    e.preventDefault();
    if (!newDocket.trim() || !newTitle.trim()) {
      setCreateError('Docket number and title are required.');
      return;
    }

    setCreating(true);
    setCreateError('');

    try {
      const response = await courtAPI.createCourtCase({
        docket_number: newDocket.trim(),
        title: newTitle.trim(),
        status: 'active',
      });
      const created = normalizeCourtCase(
        response.data?.data ||
          response.data || {
            docket_number: newDocket.trim(),
            title: newTitle.trim(),
          }
      );
      setCases((prev) => [created, ...prev.filter((c) => c.docket_number !== created.docket_number)]);
      setShowCreateForm(false);
      setNewDocket('');
      setNewTitle('');
      setUsingFallback(false);
    } catch {
      const localCase = normalizeCourtCase({
        id: newDocket.trim(),
        docket_number: newDocket.trim(),
        title: newTitle.trim(),
        status: 'active',
        created_at: new Date().toISOString(),
        transcription_count: 0,
      });
      setCases((prev) => [localCase, ...prev]);
      setShowCreateForm(false);
      setNewDocket('');
      setNewTitle('');
      setUsingFallback(true);
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return <PageLoadingState message="Loading court cases…" />;
  }

  return (
    <>
      {usingFallback && !error && (
        <div className="md-alert md-alert-warning mb-4">
          Court cases API unavailable — showing cases inferred from linked recordings.
        </div>
      )}

      {error && <div className="md-alert md-alert-danger mb-4">{error}</div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KpiStatCard label="Total cases" value={kpiStats.total} hint="All dockets in register" icon={ScaleIcon} tone="primary" />
        <KpiStatCard label="Active" value={kpiStats.active} hint="Currently open matters" icon={CheckCircleIcon} tone="success" />
        <KpiStatCard label="Closed" value={kpiStats.closed} hint="Disposed or archived" icon={ScaleIcon} tone="secondary" />
        <KpiStatCard label="Pending" value={kpiStats.pending} hint="Awaiting next listing" icon={ClockIcon} tone="warning" />
      </div>

      {showCreateForm && (
        <form onSubmit={handleCreateCase} className="md-card md-card-body mb-4">
          <h2 className="text-base font-semibold text-matdash-dark mb-4">Register new case</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="new-docket" className="block text-sm font-medium text-matdash-dark mb-1">
                Docket number <span className="text-matdash-danger">*</span>
              </label>
              <input
                id="new-docket"
                value={newDocket}
                onChange={(e) => setNewDocket(e.target.value)}
                placeholder="e.g. CR/0124/2026"
                className="md-form-control"
              />
            </div>
            <div>
              <label htmlFor="new-title" className="block text-sm font-medium text-matdash-dark mb-1">
                Case title <span className="text-matdash-danger">*</span>
              </label>
              <input
                id="new-title"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="e.g. Republic v. Mensah"
                className="md-form-control"
              />
            </div>
          </div>
          {createError && <p className="text-sm text-red-600 mt-2">{createError}</p>}
          <div className="flex gap-2 mt-4">
            <button type="submit" disabled={creating} className="md-btn md-btn-primary md-btn-sm">
              {creating ? 'Saving…' : 'Save case'}
            </button>
            <button
              type="button"
              onClick={() => setShowCreateForm(false)}
              className="md-btn md-btn-outline md-btn-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className={`md-card overflow-hidden ${dark ? '!bg-gray-900 !border-gray-800' : ''}`}>
        <div className={`px-4 py-3 border-b flex flex-wrap items-center gap-2 ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
          <div className="relative w-full max-w-sm min-w-[180px] shrink">
            <MagnifyingGlassIcon className="h-4 w-4 text-matdash-muted absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search docket or title..."
              className={`${searchCls} pl-9 h-9 py-1.5`}
            />
          </div>

          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className={`${dateCls} h-9 py-1.5`}
            aria-label="From date"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className={`${dateCls} h-9 py-1.5`}
            aria-label="To date"
          />

          <div className="ml-auto flex items-center gap-2 shrink-0">
            <div className="relative group">
              <button type="button" className={toolBtn} aria-label="Filters">
                <FunnelIcon className="h-4 w-4" />
                {activeFilterCount > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-matdash-primary text-white text-[10px] font-semibold">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              <div className={dropdownPanel}>
                <div className={dropdownInner}>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-matdash-muted">Status</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className={`${inputCls} w-full`}
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button type="button" onClick={clearFilters} className="text-xs text-matdash-primary">
                    Clear filters
                  </button>
                </div>
              </div>
            </div>

            <div className="relative group">
              <button type="button" className={toolBtn} aria-label="Column visibility">
                <Cog6ToothIcon className="h-4 w-4" />
              </button>
              <div className={dropdownPanel}>
                <div className={`w-52 py-2 rounded-xl border shadow-lg ${dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
                  {COLUMN_DEFS.map((col) => (
                    <label
                      key={col.key}
                      className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${
                        dark ? 'text-gray-200 hover:bg-gray-800' : 'text-matdash-dark hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={visibleColumns.includes(col.key)}
                        onChange={() => toggleColumn(col.key)}
                        className="rounded border-gray-300 text-matdash-primary"
                      />
                      {col.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="relative group">
              <button type="button" className={toolBtn} aria-label="Export">
                <ArrowDownTrayIcon className="h-4 w-4" />
                <span className="ml-1.5 hidden sm:inline">Export</span>
              </button>
              <div className={dropdownPanel}>
                <div className={`w-44 py-1 rounded-xl border shadow-lg ${dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
                  <button
                    type="button"
                    onClick={exportCsv}
                    className={`w-full text-left px-3 py-2 text-sm ${dark ? 'text-gray-200 hover:bg-gray-800' : 'text-matdash-dark hover:bg-gray-50'}`}
                  >
                    Export as CSV
                  </button>
                  <button
                    type="button"
                    onClick={exportJson}
                    className={`w-full text-left px-3 py-2 text-sm ${dark ? 'text-gray-200 hover:bg-gray-800' : 'text-matdash-dark hover:bg-gray-50'}`}
                  >
                    Export as JSON
                  </button>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowCreateForm((v) => !v)}
              className="inline-flex items-center justify-center h-9 px-3 rounded-lg bg-matdash-primary text-white text-sm font-medium hover:bg-blue-600 transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              <span className="ml-1.5 hidden sm:inline">New case</span>
            </button>
          </div>
        </div>

        <div className={`px-4 py-2 border-b text-xs ${dark ? 'border-gray-800 text-gray-400' : 'border-matdash-border text-gray-500'}`}>
          {filteredCases.length} case{filteredCases.length !== 1 ? 's' : ''}
          {usingFallback ? ' · derived from session records' : ''}
        </div>

        <div className="overflow-x-auto">
          <table className="md-table min-w-full">
            <thead>
              <tr>
                {showCol('docket') && <th>Docket</th>}
                {showCol('title') && <th>Title</th>}
                {showCol('status') && <th>Status</th>}
                {showCol('filed') && <th>Filed</th>}
                {showCol('sessions') && <th>Sessions</th>}
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 && (
                <tr>
                  <td colSpan={visibleColumns.length || 1} className="text-center text-matdash-muted py-10">
                    No cases match your filters.
                  </td>
                </tr>
              )}
              {pageRows.map((courtCase) => (
                <tr
                  key={courtCase.id}
                  onClick={() => navigate(caseDetailPath(courtCase))}
                  className="cursor-pointer hover:bg-matdash-primary-subtle/30"
                >
                  {showCol('docket') && (
                    <td className="font-semibold whitespace-nowrap text-matdash-primary">
                      {courtCase.docket_number}
                    </td>
                  )}
                  {showCol('title') && <td className="min-w-[220px]">{courtCase.title}</td>}
                  {showCol('status') && (
                    <td>
                      <span className={`inline-flex px-2.5 py-0.5 rounded-md text-xs font-medium capitalize ${statusBadgeClass(courtCase.status)}`}>
                        {courtCase.status}
                      </span>
                    </td>
                  )}
                  {showCol('filed') && (
                    <td className="text-matdash-muted whitespace-nowrap">{formatDate(courtCase.created_at) || '—'}</td>
                  )}
                  {showCol('sessions') && (
                    <td className="text-matdash-muted">{courtCase.transcription_count ?? '—'}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={`px-4 py-3 border-t flex flex-wrap items-center justify-between gap-3 ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
          <p className="text-xs text-matdash-muted mb-0">
            Showing {filteredCases.length === 0 ? 0 : (page - 1) * perPage + 1} to {Math.min(page * perPage, filteredCases.length)} of {filteredCases.length} cases
          </p>
          <div className="flex items-center gap-2">
            <select
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
              className={`h-8 px-2 text-xs rounded-lg border ${dark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200'}`}
            >
              {[10, 25, 50].map((n) => (
                <option key={n} value={n}>{n} / page</option>
              ))}
            </select>
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="w-8 h-8 text-xs rounded border border-matdash-border disabled:opacity-40">‹</button>
            <span className="text-xs text-matdash-muted px-1">Page {page} of {totalPages}</span>
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-8 h-8 text-xs rounded border border-matdash-border disabled:opacity-40">›</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CourtCases;
